import { useCallback, useRef, useEffect } from "react";
import Global from "../../globals/globals";
import generateRandomString from "../../MSRP/utilities/generators";
import type { MessageData } from "@/types/chat.types";

interface UseSeenMessagesProps {
  session_key: string;
  messages: MessageData[];
  wrapper: any;
  maxVisibleMessageId: string | null;
  isAtBottom: boolean;
}

interface UseSeenMessagesResult {
  markMessagesAsSeen: (immediate?: boolean) => void;
  cancelPending: () => void;
  handleFocus: () => void;
  handleBlur: () => void;
  handleNewMessageAtBottom: () => void;
}

const DEBOUNCE_MS = 750;

export function useSeenMessages({
  session_key,
  messages,
  wrapper,
  maxVisibleMessageId,
  isAtBottom
}: UseSeenMessagesProps): UseSeenMessagesResult {
  // Track last sent message ID to prevent duplicates
  const lastSentMessageIdRef = useRef<string | null>(null);
  // Debounce timer ref
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  // Track if window is focused
  const isFocusedRef = useRef<boolean>(document.hasFocus());
  // Store pending message ID when blurred
  const pendingMessageIdRef = useRef<string | null>(null);

  // Clear refs when room changes
  useEffect(() => {
    lastSentMessageIdRef.current = null;
    pendingMessageIdRef.current = null;
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, [session_key]);

  // Check if a message is unread by current user
  const isMessageUnread = useCallback((messageId: string): boolean => {
    const message = messages.find(m => m.messageId === messageId);
    if (!message || !message.message_status) return false;

    const currentUserFullName = Global.username;
    const currentUserStatus = message.message_status.find(
      (status) => status.user?.agent_username === currentUserFullName
    );

    return currentUserStatus?.message_read_at === null;
  }, [messages]);

  // Send the message-seen event
  const sendSeenEvent = useCallback((messageId: string) => {
    if (!session_key || !wrapper || !messageId) return;

    // Find the message to check if it's our own message
    const message = messages.find(m => m.messageId === messageId);

    // Skip if message is from current user (don't send seen for own messages)
    if (message?.isOwner) {
      console.log("Skipping seen event - own message:", messageId);
      return;
    }

    // Skip if already sent for this message in this session
    // This prevents duplicate sends - backend should also be idempotent
    if (lastSentMessageIdRef.current === messageId) {
      console.log("Skipping duplicate message-seen for:", messageId);
      return;
    }

    const now = Math.floor(Date.now() / 1000);

    const packet = {
      id: generateRandomString(16),
      type: "publish",
      event: "message-seen",
      publisher: Global.username + Global.companyDomain,
      parameters: {
        "seen-time": now,
        "last-message-id": messageId,
        "session-key": session_key,
      },
    };

    console.log("Sending message-seen for last-message-id:", messageId);
    wrapper.sendMsg(
      JSON.stringify(packet, null, 4),
      "application/rechat.cntl+json"
    );

    lastSentMessageIdRef.current = messageId;
  }, [session_key, wrapper, messages]);

  // Cancel any pending debounced call
  const cancelPending = useCallback(() => {
    if (debounceTimerRef.current) {
      console.log("🚫 Cancelled pending message-seen event");
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, []);

  // Main function to mark messages as seen (Telegram-style, with focus check)
  const markMessagesAsSeen = useCallback((immediate: boolean = false) => {
    if (!maxVisibleMessageId) {
      return;
    }

    if (!isFocusedRef.current) {
      console.log("Skipping seen - window not focused, storing as pending");
      pendingMessageIdRef.current = maxVisibleMessageId;
      return;
    }

    // Cancel any pending debounce
    cancelPending();

    if (immediate) {
      sendSeenEvent(maxVisibleMessageId);
    } else {
      // Debounce the send
      debounceTimerRef.current = setTimeout(() => {
        sendSeenEvent(maxVisibleMessageId);
        debounceTimerRef.current = null;
      }, DEBOUNCE_MS);
    }
  }, [maxVisibleMessageId, sendSeenEvent, cancelPending]);

  // Handle window focus
  const handleFocus = useCallback(() => {
    isFocusedRef.current = true;

    // If there's a pending message, send it now
    if (pendingMessageIdRef.current) {
      console.log("📤 Window focused, sending pending message-seen");
      sendSeenEvent(pendingMessageIdRef.current);
      pendingMessageIdRef.current = null;
    } else if (maxVisibleMessageId) {
      // Re-evaluate and send if there are unread visible messages
      markMessagesAsSeen(false);
    }
  }, [maxVisibleMessageId, sendSeenEvent, markMessagesAsSeen]);

  // Handle window blur
  const handleBlur = useCallback(() => {
    isFocusedRef.current = false;

    // Cancel pending and store the message ID
    if (debounceTimerRef.current) {
      cancelPending();
      pendingMessageIdRef.current = maxVisibleMessageId;
    }
  }, [maxVisibleMessageId, cancelPending]);

  // Handle new message arriving when at bottom (Telegram-style)
  // Only mark as seen if tab is focused
  const handleNewMessageAtBottom = useCallback(() => {
    if (!isAtBottom) {
      console.log("Skipping seen - not at bottom");
      return;
    }

    // Find the newest message (last in array)
    const newestMessage = messages[messages.length - 1];
    if (!newestMessage) return;
    console.log("New message received at bottom:", newestMessage);
    // Skip if message is from current user (don't send seen for own messages)
    if (newestMessage.isOwner) {
      console.log("Skipping seen - own message");
      return;
    }

    // Telegram-style: Only mark as seen if window is focused
    if (!isFocusedRef.current) {
      console.log("Skipping seen - window not focused, storing as pending");
      pendingMessageIdRef.current = newestMessage.messageId;
      return;
    }

    // Send immediately for new messages at bottom when focused
    console.log("New message at bottom, sending immediate message-seen");
    sendSeenEvent(newestMessage.messageId);
  }, [isAtBottom, messages, sendSeenEvent]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return {
    markMessagesAsSeen,
    cancelPending,
    handleFocus,
    handleBlur,
    handleNewMessageAtBottom
  };
}
